import React, { useState, useEffect } from 'react';
import { ChevronRight, Brain } from 'lucide-react';

function parseThoughtCall(call) {
  const raw = call.function?.arguments || '';
  let reasoning = '';

  try {
    const args = JSON.parse(raw);
    reasoning = args.reasoning_text || '';
  } catch {
    const key = '"reasoning_text":"';
    const idx = raw.indexOf(key);
    if (idx >= 0) {
      let fragment = raw.substring(idx + key.length);
      fragment = fragment.replace(/"\s*,\s*"[a-z_]*"\s*:.*$/s, '');
      fragment = fragment.replace(/"\s*\}\s*$/, '');
      fragment = fragment.replace(/"$/, '');
      fragment = fragment
        .replace(/\\n/g, '\n')
        .replace(/\\"/g, '"')
        .replace(/\\t/g, '\t')
        .replace(/\\\\/g, '\\');
      reasoning = fragment || '';
    }
  }

  return {
    id: call.id,
    reasoning,
    isActive: call.status === 'reasoning',
  };
}

export default function ThinkingPanel({ reasonCalls, isStreaming = false }) {
  const [open, setOpen] = useState(false);

  const thoughts = (Array.isArray(reasonCalls) ? reasonCalls : []).map(
    parseThoughtCall,
  );

  const hasActiveThought = thoughts.some((t) => t.isActive);
  const isActive = hasActiveThought || isStreaming;

  useEffect(() => {
    if (isActive) setOpen(true);
  }, [isActive]);

  if (!thoughts.length && !isStreaming) return null;

  const stepCount = thoughts.filter((t) => t.reasoning).length;

  return (
    <div className="mb-3 w-full max-w-3xl">
      <button
        onClick={() => setOpen((p) => !p)}
        className="group flex items-center gap-1.5 py-0.5 text-xs text-zinc-500 transition-colors hover:text-zinc-300"
        aria-expanded={open}
      >
        {isActive ? (
          <span className="relative flex h-3 w-3 shrink-0 items-center justify-center">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-violet-400 opacity-30" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-violet-400" />
          </span>
        ) : (
          <Brain className="h-3.5 w-3.5 shrink-0 text-zinc-500 group-hover:text-zinc-400" />
        )}
        <span className="font-medium">
          {isActive
            ? 'Thinking...'
            : stepCount > 1
              ? `Thought · ${stepCount} steps`
              : 'Thought'}
        </span>
        <ChevronRight
          className={`h-3 w-3 shrink-0 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
        />
      </button>

      {open && (
        <div className="mt-2 flex gap-3 pl-0.5">
          <div className="w-px shrink-0 self-stretch rounded-full bg-zinc-700" />
          <div className="max-h-64 min-w-0 flex-1 overflow-y-auto">
            {thoughts.length === 0 ? (
              <div className="flex items-center gap-1.5">
                {[0, 150, 300].map((delay) => (
                  <span
                    key={delay}
                    className="h-1.5 w-1.5 animate-bounce rounded-full bg-violet-400"
                    style={{ animationDelay: `${delay}ms` }}
                  />
                ))}
              </div>
            ) : (
              thoughts.map((thought, idx) => (
                <div
                  key={thought.id || idx}
                  className={
                    idx > 0 ? 'mt-3 border-t border-zinc-800/60 pt-3' : ''
                  }
                >
                  <p className="whitespace-pre-wrap break-words text-xs leading-relaxed text-zinc-400">
                    {thought.reasoning || (thought.isActive ? '...' : '')}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
